import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  Undo2,
  CheckCircle2,
  Calendar,
  Link2,
  AlertCircle,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { EventStatusBadge } from "../../components/ui/EventStatusBadge";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { FlaggerMultiSelect, useFlaggerFilter } from "../../components/ui/FlaggerMultiSelect";
import {
  ComparisonGrid,
  EventCardSection,
  EventOccurrences,
  EventUrls,
  MergeResolutionBadge,
  MergedDuplicateCard,
  formatDuplicateDate as formatDate,
} from "../../components/ui/DuplicateReview";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { EntityLink } from "../../components/ui/EntityLink";
import { eventsApi } from "../../utils/api";
import { displayLabel } from "../../utils/helpers";
import EventDisplayImage from "./EventDisplayImage";

const formatDateTime = (isoStr) => {
  if (!isoStr) return "—";
  try {
    return new Date(isoStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoStr;
  }
};

function ResolutionDetails({ resolution }) {
  const isAdmin = resolution.method === "admin";
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-xs space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <MergeResolutionBadge resolution={resolution} adminLabel="Admin merge" automaticLabel="Automatic (pipeline)" />
        {resolution.resolved_at && (
          <span className="text-muted-foreground">on {formatDateTime(resolution.resolved_at)}</span>
        )}
      </div>
      {isAdmin && resolution.resolved_by_email && (
        <p className="text-muted-foreground">
          By <span className="font-medium text-foreground">{resolution.resolved_by_email}</span>
        </p>
      )}
      {resolution.similarity_score != null && (
        <p className="text-muted-foreground">
          Similarity: <span className="font-medium text-foreground">{Math.round(resolution.similarity_score * 100)}%</span>
          {resolution.reason && <span> · {resolution.reason}</span>}
        </p>
      )}
      {resolution.resolution_note && (
        <p className="text-muted-foreground truncate" title={resolution.resolution_note}>
          Note: {resolution.resolution_note}
        </p>
      )}
    </div>
  );
}

function EventCard({ event, role }) {
  if (!event) {
    return (
      <div className="min-w-0 rounded-lg border border-dashed border-border bg-muted/20 p-4 flex items-center justify-center">
        <p className="text-xs text-muted-foreground italic">Canonical event no longer exists</p>
      </div>
    );
  }

  return (
    <div className={`min-w-0 rounded-lg border p-4 ${
      role === "duplicate" ? "border-red-200 bg-red-50/30" : "border-green-200 bg-green-50/30"
    }`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
          role === "duplicate" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
        }`}>
          {role === "duplicate" ? "Duplicate" : "Canonical (kept)"}
        </span>
        <EventStatusBadge status={event.display_status || event.status} />
      </div>
      {event.image && (
        <EventDisplayImage
          src={event.image}
          alt={event.title}
          imageResolutionStatus={event.image_resolution_status}
          className="mb-3 h-24 w-full rounded"
        />
      )}
      {event.venue_name && (
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
          <EntityLink type="venue" id={event.venue_id} newTab>{event.venue_name}</EntityLink>
        </p>
      )}
      <div className="mb-2">
        <h4 className="font-semibold text-sm leading-snug">{displayLabel(event.preferred_title, event.title)}</h4>
        <ProviderLabel preferred={event.preferred_title} original={event.title} />
      </div>
      {event.local_date && (
        <p className="text-xs text-muted-foreground mb-3">
          {formatDate(event.local_date)}{event.local_time ? ` · ${event.local_time}` : ""}
        </p>
      )}
      <EventCardSection icon={Calendar} title="Occurrences">
        <EventOccurrences occurrences={event.occurrences || []} maxHeight="max-h-44" />
      </EventCardSection>
      <EventCardSection icon={Link2} title="Event URLs">
        <EventUrls urls={event.urls || []} />
      </EventCardSection>
    </div>
  );
}

function FlaggedPair({ pair, onUndoSuccess }) {
  const [undoModalOpen, setUndoModalOpen] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [undoError, setUndoError] = useState(null);

  const { duplicate_event, canonical_event, live_canonical_event, resolution } = pair;
  // Merges are undone in reverse order (#667): the API says when it would refuse this undo, whether
  // or not the canonical's chain could be followed to a live event.
  const undoBlocked = Boolean(pair.undo_blocked && canonical_event);
  const undoBlockedReason = "Its canonical was merged into another event since: undo that merge first";
  // Every mention of the pair reads the curated title, same as the events list it came from.
  const duplicateTitle = displayLabel(duplicate_event.preferred_title, duplicate_event.title);

  const handleUndo = async () => {
    setUndoLoading(true);
    setUndoError(null);
    try {
      const result = await eventsApi.undoDuplicate(duplicate_event.id);
      setUndoModalOpen(false);
      const removed = result.occurrences_removed_from_canonical || 0;
      const msg = removed > 0
        ? `"${duplicateTitle}" restored to active · ${removed} absorbed occurrence${removed !== 1 ? "s" : ""} removed from canonical`
        : `"${duplicateTitle}" restored to active`;
      onUndoSuccess?.(msg);
    } catch (e) {
      setUndoError(e?.response?.data?.detail || "Undo failed");
    } finally {
      setUndoLoading(false);
    }
  };

  return (
    <>
      <MergedDuplicateCard
        icon={<AlertCircle className="h-4 w-4 shrink-0 text-red-400" />}
        title={
          <>
            <span className="text-red-700">{duplicateTitle}</span>
            <span className="mx-1.5 font-normal text-muted-foreground">→</span>
            <span>
              {canonical_event ? (
                displayLabel(canonical_event.preferred_title, canonical_event.title)
              ) : (
                <em className="text-muted-foreground">deleted event</em>
              )}
            </span>
            {live_canonical_event && (
              // The canonical was merged away since (#667): the show lives on here.
              <>
                <span className="mx-1.5 font-normal text-muted-foreground">→</span>
                <span title="The canonical was merged into this event since">
                  {displayLabel(live_canonical_event.preferred_title, live_canonical_event.title)}
                </span>
              </>
            )}
          </>
        }
        meta={
          <>
            <MergeResolutionBadge resolution={resolution} adminLabel="Admin merge" automaticLabel="Automatic (pipeline)" />
            {resolution.resolved_at && (
              <span className="text-xs text-muted-foreground">{formatDateTime(resolution.resolved_at)}</span>
            )}
          </>
        }
        action={
          // A disabled button takes no pointer events, so the reason sits on a wrapper.
          <span title={undoBlocked ? undoBlockedReason : undefined}>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setUndoModalOpen(true)}
              disabled={undoBlocked}
              aria-describedby={undoBlocked ? `undo-blocked-${duplicate_event.id}` : undefined}
            >
              <Undo2 className="h-3.5 w-3.5" />
              Undo
            </Button>
            {undoBlocked && (
              <span id={`undo-blocked-${duplicate_event.id}`} className="sr-only">
                {undoBlockedReason}
              </span>
            )}
          </span>
        }
      >
        <ResolutionDetails resolution={resolution} />
        {undoBlocked && (
          <p className="mb-3 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
            Its canonical{" "}
            <EntityLink type="event" id={canonical_event.id} newTab className="font-medium">
              {displayLabel(canonical_event.preferred_title, canonical_event.title)}
            </EntityLink>{" "}
            has been merged into{" "}
            {live_canonical_event ? (
              <EntityLink type="event" id={live_canonical_event.id} newTab className="font-medium">
                {displayLabel(live_canonical_event.preferred_title, live_canonical_event.title)}
              </EntityLink>
            ) : (
              "another event (its chain could not be followed)"
            )}{" "}
            since. To undo this merge, undo that one first.
          </p>
        )}
        <ComparisonGrid
          left={<EventCard event={duplicate_event} role="duplicate" />}
          right={<EventCard event={canonical_event} role="canonical" />}
          separator="→"
        />
      </MergedDuplicateCard>

      {/* Undo confirmation modal */}
      <Modal open={undoModalOpen} onOpenChange={setUndoModalOpen}>
        <ModalContent
          title="Undo duplicate flag"
          description={`This will separate "${duplicateTitle}" from its canonical event and restore it to active status.`}
        >
          <div className="mt-3 rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2.5 text-sm text-yellow-800 space-y-1">
            <p className="font-medium">What will happen</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li><span className="font-medium">{duplicateTitle}</span> → status set to <code className="bg-yellow-100 px-1 rounded">active</code></li>
              <li>The <code className="bg-yellow-100 px-1 rounded">duplicate_of</code> link will be cleared</li>
              <li>Occurrences absorbed into the canonical event will be removed from it</li>
              <li>Event-level URLs absorbed into the canonical event will be removed from it</li>
            </ul>
          </div>
          {undoError && <p className="mt-3 text-sm text-destructive">{undoError}</p>}
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setUndoModalOpen(false)} disabled={undoLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleUndo} disabled={undoLoading}>
              {undoLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Undo2 className="mr-2 h-4 w-4" />
              )}
              Undo duplicate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default function FlaggedDuplicatesView() {
  const [pairs, setPairs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const { searchInput, setSearchInput, search: searchFilter, submitSearch, clearSearch } = useSearchFilter();
  const {
    flaggers,
    selectedFlaggerIds: flaggedBySelection,
    setSelectedFlaggerIds: setFlaggedBySelection,
    flaggedByParam,
    syncFlaggers,
  } = useFlaggerFilter();

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const loadPairs = useCallback(async (pageNum = 1, search = "", flaggedBy = "") => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: pageNum, page_size: 20 };
      if (search) params.search = search;
      if (flaggedBy) params.flagged_by = flaggedBy;
      const data = await eventsApi.getFlaggedDuplicates(params);
      setPairs(data.pairs || []);
      setPagination(data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 });
      syncFlaggers(data.flaggers);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load merged events.");
    } finally {
      setLoading(false);
    }
  }, [syncFlaggers]);

  useEffect(() => {
    if (flaggedByParam === null) {
      setPairs([]);
      setPagination({ page: 1, page_size: 20, total: 0, total_pages: 1 });
      setLoading(false);
      return;
    }
    loadPairs(page, searchFilter, flaggedByParam);
  }, [loadPairs, page, searchFilter, flaggedByParam]);

  const handleSearch = (e) => {
    setPage(1);
    submitSearch(e);
  };

  const handleClearSearch = () => {
    setPage(1);
    clearSearch();
  };

  // After undo: if the page just emptied and we're past page 1, step back; otherwise reload.
  const handleUndoSuccess = (msg) => {
    showToast(msg);
    if (pairs.length === 1 && page > 1) {
      setPage((p) => p - 1);
    } else if (flaggedByParam !== null) {
      loadPairs(page, searchFilter, flaggedByParam);
    }
  };

  const handleFlaggedByChange = (next) => {
    setPage(1);
    setFlaggedBySelection(next);
  };

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Merged events</h2>
          <p className="text-sm text-muted-foreground">
            Events retained as duplicates — who merged them and how, with an option to undo pair by pair.
          </p>
        </div>
        <SearchFilterForm
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={handleSearch}
          onClear={handleClearSearch}
          placeholder="Search by title…"
          loading={loading}
        >
          <FlaggerMultiSelect
            flaggers={flaggers}
            selected={flaggedBySelection}
            onChange={handleFlaggedByChange}
          />
        </SearchFilterForm>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {toast && (
        <div className="rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {toast}
        </div>
      )}

      {loading && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin" />
          <p className="font-medium">Loading merged events…</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="text-sm text-muted-foreground">
            {pagination.total === 0
              ? "No merged events"
              : `${pagination.total} merged event${pagination.total !== 1 ? "s" : ""}`}
            {searchFilter && <span> matching &ldquo;{searchFilter}&rdquo;</span>}
            {flaggedByParam === null && <span> · no flaggers selected</span>}
            {flaggedByParam && (
              <span>
                {" · merged by "}
                {flaggedBySelection.length === 1
                  ? flaggers.find((f) => f.id === flaggedBySelection[0])?.label || flaggedBySelection[0]
                  : `${flaggedBySelection.length} selected`}
              </span>
            )}
          </div>

          {pairs.length > 0 && (
            <div className="space-y-3">
              {pairs.map((pair) => (
                <FlaggedPair
                  key={pair.duplicate_event?.id}
                  pair={pair}
                  onUndoSuccess={handleUndoSuccess}
                />
              ))}
            </div>
          )}

          {pairs.length === 0 && !loading && pagination.total === 0 && (
            <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
              <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-green-500" />
              <p className="font-medium">
                {searchFilter ? `No results for "${searchFilter}"` : "No merged events"}
              </p>
              <p className="text-sm mt-1">
                {searchFilter
                  ? "Try a different search term."
                  : "No events are currently marked as duplicates."}
              </p>
            </div>
          )}

          {/* Pagination */}
          <Pagination page={pagination.page} totalPages={pagination.total_pages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
